import React from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  useTheme
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const RightSidePanel = ({
  open,
  logs,
  logSearchQuery,
  setLogSearchQuery,
  logTypeFilter,
  toggleLogTypeFilter,
  filteredLogs,
  handleLogClick,
  formatTimestamp,
  logDivRef,
  hasInstructionBar = false,
  isMaximized = false
}) => {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          position: isMaximized ? 'fixed' : 'absolute',
          right: open ? 0 : '-400px',
          top: isMaximized ? '64px' : '88px', // When maximized: 32px top bar + 32px tab bar = 64px. When not maximized: 40px top bar + ~48px tab row = 88px
          bottom: 0,
          width: '400px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
          transition: 'right 0.3s ease',
          zIndex: isMaximized ? 2000 : 10, // Higher z-index when maximized to appear above content
          boxShadow: open ? '-2px 0 8px rgba(0,0,0,0.1)' : 'none'
        }}
      >
        {/* Logs Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              {/* Search box and filters at the top */}
              <Box
                sx={{
                  padding: '12px',
                  borderBottom: 1,
                  borderColor: 'divider',
                  backgroundColor: 'background.default',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  flexShrink: 0
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search logs..."
                  value={logSearchQuery}
                  onChange={(e) => setLogSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                    endAdornment: logSearchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setLogSearchQuery('')}
                          sx={{ padding: '4px' }}
                        >
                          <ClearIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      fontSize: '13px',
                      backgroundColor: 'background.paper',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />

                {/* Log type filter chips */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<InfoIcon sx={{ fontSize: '16px' }} />}
                    label="Info"
                    size="small"
                    onClick={() => toggleLogTypeFilter('info')}
                    color={logTypeFilter.includes('info') ? 'primary' : 'default'}
                    variant={logTypeFilter.includes('info') ? 'filled' : 'outlined'}
                    sx={{ fontSize: '11px', height: '24px' }}
                  />
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: '16px' }} />}
                    label="Warning"
                    size="small"
                    onClick={() => toggleLogTypeFilter('warning')}
                    color={logTypeFilter.includes('warning') ? 'warning' : 'default'}
                    sx={{
                      fontSize: '11px',
                      height: '24px'
                    }}
                    variant={logTypeFilter.includes('warning') ? 'filled' : 'outlined'}
                  />
                  <Chip
                    icon={<ErrorIcon sx={{ fontSize: '16px' }} />}
                    label="Error"
                    size="small"
                    onClick={() => toggleLogTypeFilter('error')}
                    color={logTypeFilter.includes('error') ? 'error' : 'default'}
                    sx={{
                      fontSize: '11px',
                      height: '24px'
                    }}
                    variant={logTypeFilter.includes('error') ? 'filled' : 'outlined'}
                  />
                  <Chip
                    icon={<PlayArrowIcon sx={{ fontSize: '16px' }} />}
                    label="Action"
                    size="small"
                    onClick={() => toggleLogTypeFilter('action')}
                    color={logTypeFilter.includes('action') ? 'secondary' : 'default'}
                    sx={{
                      fontSize: '11px',
                      height: '24px'
                    }}
                    variant={logTypeFilter.includes('action') ? 'filled' : 'outlined'}
                  />
                </Box>

                {(logSearchQuery || logTypeFilter.length < 4) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Showing {filteredLogs.length} of {logs.length} logs
                  </Typography>
                )}
              </Box>

              <Box
                ref={logDivRef}
                sx={{
                  flex: 1,
                  padding: '12px',
                  overflowY: 'auto',
                  backgroundColor: 'background.paper'
                }}
              >
                {filteredLogs.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredLogs.map((log) => {
                      const getLogStyles = () => {
                        const isDark = theme.palette.mode === 'dark';
                        switch (log.type) {
                          case 'error':
                            return {
                              icon: <ErrorIcon sx={{ color: 'error.main', fontSize: '20px' }} />,
                              textColor: 'error.main',
                              bgColor: isDark ? 'rgba(211, 47, 47, 0.15)' : 'error.lighter',
                              borderColor: theme.palette.error.main
                            };
                          case 'warning':
                            return {
                              icon: <WarningIcon sx={{ color: 'warning.main', fontSize: '20px' }} />,
                              textColor: 'warning.main',
                              bgColor: isDark ? 'rgba(237, 108, 2, 0.15)' : 'warning.lighter',
                              borderColor: theme.palette.warning.main
                            };
                          case 'action':
                            return {
                              icon: <PlayArrowIcon sx={{ color: 'secondary.main', fontSize: '20px' }} />,
                              textColor: 'secondary.main',
                              bgColor: isDark ? 'rgba(156, 39, 176, 0.15)' : 'secondary.lighter',
                              borderColor: theme.palette.secondary.main
                            };
                          case 'info':
                          default:
                            return {
                              icon: <InfoIcon sx={{ color: 'primary.main', fontSize: '20px' }} />,
                              textColor: 'primary.main',
                              bgColor: isDark ? 'rgba(25, 118, 210, 0.15)' : 'primary.lighter',
                              borderColor: theme.palette.primary.main
                            };
                        }
                      };

                      const styles = getLogStyles();

                      return (
                        <Box
                          key={log.id}
                          onClick={() => handleLogClick(log.timestamp)}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            padding: '12px',
                            backgroundColor: styles.bgColor,
                            borderLeft: `3px solid ${styles.borderColor}`,
                            borderRadius: '4px',
                            alignItems: 'flex-start',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateX(-4px)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <Box sx={{ mt: 0.5 }}>{styles.icon}</Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: styles.textColor,
                                fontWeight: 500,
                                fontSize: '13px',
                                mb: 0.5
                              }}
                            >
                              {formatTimestamp(log.timestamp)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.primary',
                                fontSize: '14px',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}
                            >
                              {log.content}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : logs.length > 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
                    No logs match your search
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
                    Waiting for logs...
                  </Typography>
                )}
              </Box>
        </Box>
      </Box>
    </>
  );
};

export default RightSidePanel;
